<template>
    <div class="p-4">
        <div v-if="isPrepLevel" class="bg-white p-4 rounded shadow-sm border">
            <b-row>
                <b-col md="7">
                    <b-form-group :label="$t('english_proficiency_exam_question')" class="font-weight-bold">
                        <b-form-radio-group
                            v-model="prepExamStatus"
                            :options="[
                                { text: $t('yes'), value: 'yes' },
                                { text: $t('no'), value: 'no' }
                            ]"
                            stacked
                        ></b-form-radio-group>
                    </b-form-group>

                    <div class="mt-4 text-muted small">
                        <div class="font-weight-bold mb-2">{{ $t('description') }}:</div>
                        <p v-if="prepExamStatus === 'yes'">{{ $t('english_proficiency_exam_yes_desc') }}</p>
                        <p v-if="prepExamStatus === 'no'">{{ $t('english_proficiency_exam_no_desc') }}</p>
                    </div>
                </b-col>
            </b-row>
        </div>

        <div v-else>
            <div class="mb-4">
                <div class="d-flex align-items-center justify-content-between mb-2">
                    <span class="font-weight-bold">{{ $t('available_sections') }}</span>
                    <span v-if="isGraduateLevel" class="text-danger small font-weight-bold">
                        *{{ $t('graduate_course_limit_warning') }}
                    </span>
                </div>

                <div v-if="sectionsLoading" class="text-center py-4">
                    <b-spinner variant="primary" />
                </div>
                <div v-else-if="!programCode" class="text-muted small p-3 bg-light rounded border">
                    {{ $t('please_select_program_first') }}
                </div>
                <div v-else-if="availableSections.length === 0" class="text-muted small p-3 bg-light rounded border">
                    {{ $t('no_section_found') }}
                </div>
                <multi-selectbox
                    v-else
                    :value="selectedSectionKeys"
                    :options="sectionSelectboxOptions"
                    :multiple="true"
                    @input="onSelectboxChange"
                />
            </div>

            <div class="mb-4" v-if="selectedCourses.length > 0">
                <div class="font-weight-bold mb-2">{{ $t('selected_courses') }}</div>
                <b-table
                    bordered responsive small hover head-variant="light"
                    :items="selectedCourses"
                    :fields="selectedFields"
                    class="font-size-13 text-center bg-white shadow-sm"
                >
                    <template #cell(actions)="data">
                        <b-button size="sm" variant="outline-danger" @click="removeCourse(data.index)" class="font-weight-bold px-2 py-0">
                            {{ $t('delete').toUpperCase() }}
                        </b-button>
                    </template>
                </b-table>
            </div>
        </div>

        <div class="mb-4 font-size-12 text-muted font-weight-bold">
            <div v-html="$t('fee_disclaimer_message')"></div>
        </div>

        <div class="d-flex justify-content-center mt-3">
            <b-button variant="primary" @click="submit" class="px-5 font-weight-bold shadow-sm" :disabled="loading">
                <b-spinner small v-if="loading" class="mr-2" />
                {{ $t('next') }}
            </b-button>
        </div>

        <CommonModal ref="warningModal" size="md" :onHideOnlyX="true" :closeBtn="false" @bHideModalHeaderClose="cancelCourse">
            <template v-slot:CommonModalTitle>
                <div class="d-flex align-items-center text-warning">
                    <i class="ri-error-warning-line mr-2" style="font-size: 1.5rem;"></i>
                    {{ $t('warning') }}
                </div>
            </template>
            <template v-slot:CommonModalContent>
                <div class="text-center py-2">
                    <div class="font-weight-bold text-dark mb-4" style="font-size: 1.1rem; line-height: 1.6;" v-html="$t('last_course_ni_warning')"></div>
                    <div class="d-flex justify-content-center">
                        <b-button variant="outline-secondary" class="font-weight-bold shadow-sm mr-3 px-4" @click="cancelCourse">
                            {{ $t('cancel') }}
                        </b-button>
                        <b-button variant="primary" class="font-weight-bold shadow-sm px-5" @click="confirmCourse">
                            {{ $t('confirm') }}
                        </b-button>
                    </div>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
import VisitorStudentApplicationServices from "@/services/VisitorStudentApplicationServices";
import CommonModal from "@/components/elements/CommonModal.vue";
import MultiSelectbox from "@/components/interactive-fields/MultiSelectbox.vue";

export default {
    name: "CourseRegistrationTab",
    components: { CommonModal, MultiSelectbox },
    props: {
        isGraduateLevel: {
            type: Boolean,
            default: false
        },
        programLevel: {
            type: String,
            default: null
        },
        programCode: {
            type: String,
            default: null
        },
        facultyCode: {
            type: String,
            default: null
        },
        graduateType: {
            type: String,
            default: null
        },
        initialCourses: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            loading: false,
            sectionsLoading: false,
            availableSections: [],
            selectedCourses: this.initialCourses || [],
            prepExamStatus: null,
            pendingSection: null,
            pendingKeys: [],
            selectedFields: [
                { key: 'course_code', label: this.$t('course_code') },
                { key: 'course_name', label: this.$t('course_name') },
                { key: 'section', label: this.$t('section') },
                { key: 'course_type', label: this.$t('course_type') },
                { key: 'actions', label: '' }
            ]
        }
    },
    computed: {
        isPrepLevel() {
            return this.programLevel === 'H';
        },
        sectionFilters() {
            return {
                programCode: this.programCode,
                programLevel: this.programLevel,
                facultyCode: this.facultyCode,
                graduateType: this.graduateType
            };
        },
        sectionSelectboxOptions() {
            return this.availableSections.map(s => ({
                value: `${s.course_code}_${s.section}`,
                text: `${s.course_code} - ${s.course_name} (${this.$t('section')} ${s.section})`
            }));
        },
        selectedSectionKeys() {
            return this.selectedCourses.map(c => `${c.course_code}_${c.section}`);
        }
    },
    watch: {
        sectionFilters: {
            immediate: true,
            deep: true,
            handler(val) {
                if (val.programCode) {
                    this.loadSections(val.programCode);
                } else {
                    this.availableSections = [];
                }
            }
        }
    },
    methods: {
        async loadSections(programCode) {
            this.sectionsLoading = true;
            try {
                const payload = {
                    program_code: programCode,
                    program_level: this.programLevel || null,
                    faculty: this.facultyCode || null,
                    program: programCode,
                    graduate_type: this.graduateType || null
                };
                const res = await VisitorStudentApplicationServices.sectionList(payload);
                const data = res.data.data;
                this.availableSections = Array.isArray(data) ? data : (data?.items || []);
            } catch (e) {
                this.availableSections = [];
            } finally {
                this.sectionsLoading = false;
            }
        },
        sectionKey(s) {
            return `${s.course_code}_${s.section}`;
        },
        onSelectboxChange(newKeys) {
            const prevKeys = this.selectedSectionKeys;
            const added = newKeys.filter(k => !prevKeys.includes(k));
            const removed = prevKeys.filter(k => !newKeys.includes(k));

            for (const key of removed) {
                this.selectedCourses = this.selectedCourses.filter(
                    c => this.sectionKey(c) !== key
                );
            }

            for (const key of added) {
                const section = this.availableSections.find(s => this.sectionKey(s) === key);
                if (!section) continue;

                if (this.isGraduateLevel && this.selectedCourses.length >= 4) {
                    this.$toast.error(this.$t('graduate_course_limit_warning'));
                    continue;
                }

                if (this.isGraduateLevel && this.selectedCourses.length === 3) {
                    this.pendingSection = section;
                    this.pendingKeys = newKeys;
                    this.$refs.warningModal.$refs.commonModal.show();
                    continue;
                }

                this.selectedCourses.push({ ...section });
            }
        },
        confirmCourse() {
            if (this.pendingSection) {
                this.selectedCourses.push({ ...this.pendingSection, course_type: 'NI' });
                this.pendingSection = null;
                this.pendingKeys = [];
            }
            this.$refs.warningModal.$refs.commonModal.hide();
        },
        cancelCourse() {
            this.pendingSection = null;
            this.pendingKeys = [];
            this.$refs.warningModal.$refs.commonModal.hide();
        },
        removeCourse(index) {
            this.selectedCourses.splice(index, 1);
        },
        async submit() {
            if (this.isPrepLevel && !this.prepExamStatus) {
                this.$toast.error(this.$t('please_select_english_proficiency_status'));
                return;
            }

            if (!this.isPrepLevel && this.selectedCourses.length === 0) {
                this.$toast.error(this.$t('please_select_at_least_one_course'));
                return;
            }

            this.loading = true;
            try {
                const credentials = this.$store.getters['VisitorStudentApplicationStore/getCredentials'];

                const sections = this.selectedCourses.map(c => ({
                    section_id: c.id || c.section_id || null,
                    course_code: c.course_code || null,
                    course_name: c.course_name || null,
                    section: c.section || null,
                    course_type: c.course_type || null,
                    course_day: c.course_day || null,
                    course_time: c.course_time || null,
                    instructor_name: c.instructor_name || null,
                    fee: c.fee || null
                }));

                const payload = { ...credentials, sections };
                const response = await VisitorStudentApplicationServices.updateCourseRegistration(payload);
                const message = response.data?.message || response.data?.data?.message;
                if (message) this.$toast.success(this.$t('api.' + message));
                this.$emit('next', { courses: this.selectedCourses, prepExamStatus: this.prepExamStatus });
            } catch (error) {
                this.showErrors(error);
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>
